import { BlockPlanificationConstants } from "../../../Resources/Constants";

const DEFAULT_EXPANDED_HEIGHT = BlockPlanificationConstants.WORKOUT_MIN_HEIGHT;
const DEFAULT_COLLAPSED_HEIGHT = Math.round(DEFAULT_EXPANDED_HEIGHT / 2);

const HEIGHT_UPDATE_THRESHOLD_PX = 2;
const WIDTH_BUCKET_SIZE_PX = 32;
const COLLAPSED_FROM_EXPANDED_RATIO = 0.62;
const EXPANDED_FROM_COLLAPSED_RATIO = 1.65;

export const HEIGHT_CACHE_MAX_ENTRIES = 500;
export const workoutHeightCache = new Map();

const isPositiveNumber = (value) =>
  typeof value === "number" && Number.isFinite(value) && value > 0;

const getWidthBucket = (columnWidth, bucketSize = WIDTH_BUCKET_SIZE_PX) => {
  if (!isPositiveNumber(columnWidth)) return null;
  const safeBucketSize = Math.max(1, Math.round(bucketSize));
  return Math.round(columnWidth / safeBucketSize) * safeBucketSize;
};

const getModeKey = (showOnlyProtocolSlots = false) =>
  showOnlyProtocolSlots ? "onlySlots" : "all";

const getExpandedKey = (exercisesExpanded = true) =>
  exercisesExpanded ? "expanded" : "collapsed";

const getLegacyHeightKey = (exercisesExpanded = true) =>
  getExpandedKey(exercisesExpanded);

const getLegacyStateKey = (
  exercisesExpanded = true,
  showOnlyProtocolSlots = false
) => `${getModeKey(showOnlyProtocolSlots)}:${getExpandedKey(exercisesExpanded)}`;

const estimateFromOppositeStateHeight = (sourceHeight, exercisesExpanded) => {
  if (!isPositiveNumber(sourceHeight)) {
    return exercisesExpanded ? DEFAULT_EXPANDED_HEIGHT : DEFAULT_COLLAPSED_HEIGHT;
  }

  if (exercisesExpanded) {
    const estimated = Math.round(sourceHeight * EXPANDED_FROM_COLLAPSED_RATIO);
    return Math.max(DEFAULT_EXPANDED_HEIGHT, estimated);
  }

  const upperBound = Math.round(sourceHeight);
  if (upperBound <= DEFAULT_COLLAPSED_HEIGHT) {
    return upperBound;
  }

  const estimated = Math.round(sourceHeight * COLLAPSED_FROM_EXPANDED_RATIO);
  return Math.min(
    upperBound,
    Math.max(DEFAULT_COLLAPSED_HEIGHT, estimated)
  );
};

const enforceCacheLimit = (
  cache = workoutHeightCache,
  maxEntries = HEIGHT_CACHE_MAX_ENTRIES
) => {
  if (!(cache instanceof Map)) return;
  while (cache.size > maxEntries) {
    const oldestKey = cache.keys().next().value;
    if (oldestKey === undefined) break;
    cache.delete(oldestKey);
  }
};

export const getDefaultPlaceholderHeight = (exercisesExpanded = true) =>
  exercisesExpanded ? DEFAULT_EXPANDED_HEIGHT : DEFAULT_COLLAPSED_HEIGHT;

export const getHeightStateKey = (
  exercisesExpanded = true,
  showOnlyProtocolSlots = false,
  columnWidth
) => {
  const modeKey = getModeKey(showOnlyProtocolSlots);
  const expandedKey = getExpandedKey(exercisesExpanded);
  const widthBucket = getWidthBucket(columnWidth);

  if (widthBucket == null) {
    return `${modeKey}:${expandedKey}`;
  }

  return `${modeKey}:${expandedKey}:w${widthBucket}`;
};

export const hasCachedHeight = (
  workoutId,
  exercisesExpanded = true,
  showOnlyProtocolSlots = false,
  columnWidth,
  cache = workoutHeightCache
) => {
  if (!workoutId || !(cache instanceof Map)) return false;
  const cached = cache.get(workoutId);
  if (!cached) return false;

  // Backward compatibility: old entries may be a number (expanded, all mode)
  if (typeof cached === "number") {
    return exercisesExpanded && !showOnlyProtocolSlots;
  }

  const stateKey = getHeightStateKey(
    exercisesExpanded,
    showOnlyProtocolSlots,
    columnWidth
  );
  if (isPositiveNumber(cached[stateKey])) return true;

  const legacyStateKey = getLegacyStateKey(
    exercisesExpanded,
    showOnlyProtocolSlots
  );
  if (isPositiveNumber(cached[legacyStateKey])) return true;

  const legacyExpandedKey = getLegacyHeightKey(exercisesExpanded);
  return isPositiveNumber(cached[legacyExpandedKey]);
};

export const getCachedHeight = (
  workoutId,
  exercisesExpanded = true,
  showOnlyProtocolSlots = false,
  columnWidth,
  cache = workoutHeightCache
) => {
  const defaultHeight = getDefaultPlaceholderHeight(exercisesExpanded);
  if (!workoutId || !(cache instanceof Map)) return defaultHeight;

  const cached = cache.get(workoutId);
  if (!cached) return defaultHeight;

  // Backward compatibility: old entries may be a number (expanded, all mode)
  if (typeof cached === "number") {
    if (exercisesExpanded && !showOnlyProtocolSlots) {
      return cached;
    }
    return estimateFromOppositeStateHeight(cached, exercisesExpanded);
  }

  const stateKey = getHeightStateKey(
    exercisesExpanded,
    showOnlyProtocolSlots,
    columnWidth
  );
  const currentStateHeight = cached[stateKey];
  if (isPositiveNumber(currentStateHeight)) return currentStateHeight;

  const legacyStateKey = getLegacyStateKey(
    exercisesExpanded,
    showOnlyProtocolSlots
  );
  const legacyStateHeight = cached[legacyStateKey];
  if (isPositiveNumber(legacyStateHeight)) return legacyStateHeight;

  const legacyExpandedKey = getLegacyHeightKey(exercisesExpanded);
  const legacyExpandedHeight = cached[legacyExpandedKey];
  if (isPositiveNumber(legacyExpandedHeight)) return legacyExpandedHeight;

  const oppositeExactKey = getHeightStateKey(
    !exercisesExpanded,
    showOnlyProtocolSlots,
    columnWidth
  );
  const oppositeLegacyStateKey = getLegacyStateKey(
    !exercisesExpanded,
    showOnlyProtocolSlots
  );
  const oppositeLegacyExpandedKey = getLegacyHeightKey(!exercisesExpanded);

  const oppositeSourceHeight =
    cached[oppositeExactKey] ??
    cached[oppositeLegacyStateKey] ??
    cached[oppositeLegacyExpandedKey];

  if (isPositiveNumber(oppositeSourceHeight)) {
    return estimateFromOppositeStateHeight(oppositeSourceHeight, exercisesExpanded);
  }

  return defaultHeight;
};

export const setCachedHeight = (
  workoutId,
  height,
  exercisesExpanded = true,
  showOnlyProtocolSlots = false,
  columnWidth,
  cache = workoutHeightCache,
  maxEntries = HEIGHT_CACHE_MAX_ENTRIES
) => {
  if (!workoutId || !isPositiveNumber(height) || !(cache instanceof Map)) return;

  const stateKey = getHeightStateKey(
    exercisesExpanded,
    showOnlyProtocolSlots,
    columnWidth
  );
  const legacyKey = getLegacyHeightKey(exercisesExpanded);
  const current = cache.get(workoutId);

  // Backward compatibility: migrate old number entries to object structure
  if (typeof current === "number") {
    cache.set(workoutId, {
      [legacyKey]: current,
      [stateKey]: height,
    });
    enforceCacheLimit(cache, maxEntries);
    return;
  }

  const updated = current || {};
  const existingHeight = updated[stateKey];
  if (
    isPositiveNumber(existingHeight) &&
    Math.abs(existingHeight - height) <= HEIGHT_UPDATE_THRESHOLD_PX
  ) {
    return;
  }

  updated[stateKey] = height;
  cache.set(workoutId, updated);
  enforceCacheLimit(cache, maxEntries);
};

export const shouldRenderWorkoutCard = ({
  forceRender = false,
  isVisible = false,
  hasBeenVisible = false,
  hasHeight = false,
  isAboveViewport = false,
} = {}) => {
  const needsHeightMeasurement = !hasHeight && isAboveViewport;
  return forceRender || isVisible || hasBeenVisible || needsHeightMeasurement;
};

export const clearWorkoutHeightCache = (cache = workoutHeightCache) => {
  if (cache instanceof Map) {
    cache.clear();
  }
};

